<template>
  <div class="interest-calculation-create">
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>新增延期费用计算</span>
      </div>
      <div class="header-actions">
        <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </div>
    </div>

    <div class="content">
      <!-- 合同选择 -->
      <el-card class="form-card">
        <div slot="header" class="card-header">选择合同</div>
        <el-form label-width="120px">
          <el-form-item label="合同" required>
            <el-select
              v-model="selectedContractId"
              placeholder="请选择合同"
              filterable
              style="width: 400px"
              :loading="contractLoading"
              @change="onContractChange"
            >
              <el-option
                v-for="item in contractOptions"
                :key="item.id"
                :label="`${item.contractNumber || ''} - ${item.contractName || ''}`"
                :value="item.id"
              />
            </el-select>
            <span v-if="contractOptions.length === 0 && !contractLoading" class="form-tip">暂无启用计息的合同</span>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 合同信息（选择合同后展示） -->
      <el-card v-if="selectedContract" class="info-card">
        <div slot="header" class="card-header">合同信息</div>
        <el-descriptions border :column="3">
          <el-descriptions-item label="创建日期">{{ formatContractDate(selectedContract.contractDate) }}</el-descriptions-item>
          <el-descriptions-item label="是否计息">{{ selectedContract.interestCalculation ? '是' : '否' }}</el-descriptions-item>
          <el-descriptions-item label="免息天数">{{ selectedContract.interestFreeDays ?? '-' }} 天</el-descriptions-item>
          <el-descriptions-item label="利率类型">{{ selectedContract.interestRateType === 'FIXED' ? '固定利率' : '变动利率' }}</el-descriptions-item>
          <el-descriptions-item label="利率值">{{ formatRateDisplay(selectedContract) }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 延期费用截止日期与审核配置 -->
      <el-card v-if="selectedContract" class="form-card">
        <div slot="header" class="card-header">延期费用截止日期与审核配置</div>
        <el-form ref="form" :model="form" :rules="formRules" label-width="140px">
          <el-form-item label="截止日期" prop="cutoffDate" required>
            <el-date-picker
              v-model="form.cutoffDate"
              type="date"
              placeholder="请选择延期费用计算截止日期"
              value-format="yyyy-MM-dd"
              style="width: 200px"
              @change="onCutoffDateChange"
            />
          </el-form-item>
          <el-form-item label="是否需要供应商审核">
            <el-radio-group v-model="form.needSupplierAudit">
              <el-radio :label="false">不需要</el-radio>
              <el-radio :label="true">需要</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="form.needSupplierAudit" label="供应商审核人">
            <el-select
              v-model="form.supplierAuditorId"
              placeholder="请选择审核人"
              filterable
              style="width: 300px"
              :loading="supplierAuditorLoading"
              loading-text="正在查询审核人..."
            >
              <el-option
                v-for="u in supplierAuditorList"
                :key="u.id"
                :label="`${u.realName || u.username} (${u.unitName || ''})`"
                :value="u.id"
              />
            </el-select>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 结算单及延期费用预览（选择截止日期后展示） -->
      <el-card v-if="selectedContract && form.cutoffDate" class="preview-card">
        <div slot="header" class="card-header">
          <span>结算单及延期费用预览</span>
          <span v-if="previewData" class="flow-summary">共 {{ previewData.settlements?.length || 0 }} 个结算单</span>
        </div>
        <div v-if="previewLoading" class="preview-loading">
          <i class="el-icon-loading"></i> 加载中...
        </div>
        <template v-else-if="previewData">
          <el-table :data="previewData.settlements || []" border size="small">
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column label="结算单号" align="center" prop="settlementNumber" min-width="140" />
            <el-table-column label="结算单标题" min-width="160" align="center" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.settlementTitle || '-' }}</template>
            </el-table-column>
            <el-table-column label="结算时间" align="center" width="120">
              <template slot-scope="scope">{{ formatSettlementDate(scope.row.settlementDate) }}</template>
            </el-table-column>
            <el-table-column label="结算金额" align="center" width="120">
              <template slot-scope="scope">
                <span class="amount-text">{{ formatAmount(scope.row.settlementAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="已付金额" align="center" width="120">
              <template slot-scope="scope">
                <span class="amount-text">{{ formatAmount(scope.row.paidAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="未付金额" align="center" width="120">
              <template slot-scope="scope">
                <span class="amount-text">{{ formatAmount(scope.row.remainingAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="付款条数" width="90" align="center">
              <template slot-scope="scope">{{ scope.row.paymentFlowCount || 0 }} 条</template>
            </el-table-column>
            <el-table-column label="已付延期费用" align="center" width="110">
              <template slot-scope="scope">
                <span class="interest-text">{{ formatAmount(scope.row.paidInterest) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="未付延期费用" align="center" width="110">
              <template slot-scope="scope">
                <span class="unpaid-interest">{{ formatAmount(scope.row.unpaidInterest) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="延期费用金额" align="center" width="120">
              <template slot-scope="scope">
                <span class="interest-text">{{ formatAmount(scope.row.interestAmount) }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="total-interest">
            <span class="label">结算总额：</span>
            <span class="value">{{ formatAmount(previewData.settlementTotalAmount) }}</span>
            <span class="label" style="margin-left: 24px">延期费用总额：</span>
            <span class="value interest">{{ formatAmount(previewData.interestTotalAmount) }}</span>
          </div>
        </template>
        <div v-else-if="previewError" class="preview-error">
          {{ previewError }}
        </div>
      </el-card>

      <!-- 保存 -->
      <div v-if="selectedContract" class="submit-area">
        <el-button type="primary" icon="el-icon-check" :loading="saveLoading" @click="handleSave">保存</el-button>
        <el-button icon="el-icon-back" @click="goBack">取消</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { createInterestCalculation, previewInterestCalculation } from '@/api/modules/interest-calculation'
import { query_purchase_contracts_pagination, query_sales_contracts_pagination, getContractById } from '@/api/modules/contracts'
import { getUsersByUnitId, queryUsersPagination } from '@/api/modules/users'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'InterestCalculationCreate',
  data() {
    return {
      contractLoading: false,
      supplierAuditorLoading: false,
      saveLoading: false,
      previewLoading: false,
      previewData: null,
      previewError: '',
      selectedContractId: null,
      contractOptions: [],
      contractDetailMap: {},
      supplierAuditorList: [],
      currentUser: null,
      form: {
        cutoffDate: null,
        needSupplierAudit: false,
        supplierAuditorId: null
      },
      formRules: {
        cutoffDate: [{ required: true, message: '请选择截止日期', trigger: 'change' }]
      }
    }
  },
  computed: {
    selectedContract() {
      if (!this.selectedContractId) return null
      const base = this.contractOptions.find(c => c.id === this.selectedContractId)
      const detail = this.contractDetailMap[this.selectedContractId]
      return detail ? { ...base, ...detail } : base
    }
  },
  created() {
    this.getCurrentUserInfo()
    this.loadContracts()
  },
  watch: {
    'form.cutoffDate'(val) {
      if (!val) {
        this.previewData = null
        this.previewError = ''
      }
    },
    'form.needSupplierAudit'(val) {
      if (val) {
        this.getSupplierAuditorList()
      } else {
        this.form.supplierAuditorId = null
        this.supplierAuditorList = []
      }
    }
  },
  methods: {
    getCurrentUserInfo() {
      try {
        const userInfo = getUserInfo()
        if (userInfo) this.currentUser = userInfo
      } catch (err) {
        console.error('获取用户信息失败', err)
      }
    },
    async getSupplierAuditorList() {
      if (!this.currentUser) {
        this.$message.warning('当前用户信息不存在，无法获取供应商审核人列表')
        return
      }
      const roleId = this.currentUser.role?.id || this.currentUser.roleId || this.currentUser.role_id
      const unitId = this.currentUser.unitId || this.currentUser.unit_id
      const projectIds = this.currentUser.projectIds || this.currentUser.project_ids
      const currentUserId = this.currentUser.id || this.currentUser.userId
      this.supplierAuditorLoading = true
      try {
        if (roleId === 2 && unitId) {
          const res = await getUsersByUnitId(unitId)
          if (res.data && res.data.success) {
            const allUsers = res.data.data || []
            this.supplierAuditorList = allUsers.filter(u => (u.id || u.userId) !== currentUserId)
            if (this.supplierAuditorList.length === 0) {
              this.$message.warning('本单位没有其他用户可以作为供应商审核人')
            }
          }
        } else if (roleId === 3 && projectIds && projectIds.length) {
          const res = await queryUsersPagination({ projectIds: projectIds.join(','), page: 0, size: 100 })
          if (res.data && res.data.success) {
            const allUsers = res.data.data?.content || []
            this.supplierAuditorList = allUsers.filter(u => (u.id || u.userId) !== currentUserId)
            if (this.supplierAuditorList.length === 0) {
              this.$message.warning('同项目没有其他用户可以作为供应商审核人')
            }
          }
        } else {
          this.$message.warning('无法获取审核人列表，请检查用户角色配置')
        }
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || e.message || '获取审核人失败')
      } finally {
        this.supplierAuditorLoading = false
      }
    },
    async loadContracts() {
      this.contractLoading = true
      try {
        const responses = await Promise.all([
          query_purchase_contracts_pagination({ page: 1, size: 500 }),
          query_sales_contracts_pagination({ page: 1, size: 500 })
        ])
        const contracts = new Map()
        for (const { data } of responses) {
          if (!data || !data.success || !data.data) {
            throw new Error(data?.message || '加载合同列表失败')
          }
          for (const contract of data.data.content || []) {
            if (contract.interestCalculation === true) contracts.set(contract.id, contract)
          }
        }
        this.contractOptions = [...contracts.values()]
      } catch (e) {
        this.contractOptions = []
        this.$message.error((e.response && e.response.data && e.response.data.message) || e.message || '加载合同列表失败')
      } finally {
        this.contractLoading = false
      }
    },
    onContractChange() {
      this.form.cutoffDate = null
      this.form.supplierAuditorId = null
      this.previewData = null
      this.previewError = ''
      if (this.selectedContractId) {
        this.loadContractDetail()
      }
    },
    onCutoffDateChange() {
      this.previewData = null
      this.previewError = ''
      if (!this.form.cutoffDate || !this.selectedContractId) return
      this.loadPreview()
    },
    async loadPreview() {
      if (!this.selectedContractId || !this.form.cutoffDate) return
      this.previewLoading = true
      this.previewError = ''
      try {
        const res = await previewInterestCalculation({
          contractId: this.selectedContractId,
          cutoffDate: this.form.cutoffDate
        })
        const data = res.data
        if (data && data.success && data.data) {
          this.previewData = data.data
        } else {
          this.previewError = (data && data.message) || '加载预览失败'
        }
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '加载预览失败'
        this.previewError = msg
      } finally {
        this.previewLoading = false
      }
    },
    async loadContractDetail() {
      if (!this.selectedContractId || this.contractDetailMap[this.selectedContractId]) return
      try {
        const res = await getContractById(this.selectedContractId)
        const data = res.data
        if (data && data.success && data.data) {
          this.$set(this.contractDetailMap, this.selectedContractId, data.data)
        }
      } catch (e) {
        console.warn('加载合同详情失败', e)
      }
    },
    handleSave() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        if (!this.selectedContract || !this.form.cutoffDate) {
          this.$message.warning('请选择合同并设置截止日期')
          return
        }
        if (this.form.needSupplierAudit && !this.form.supplierAuditorId) {
          this.$message.warning('请选择供应商审核人')
          return
        }
        this.saveLoading = true
        try {
          const requestData = {
            contractId: this.selectedContractId,
            cutoffDate: this.form.cutoffDate,
            // 显式传递 needSupplierAudit，默认不需要审核传 false，否则后台不知道
            needSupplierAudit: this.form.needSupplierAudit === true
          }
          if (this.form.needSupplierAudit) requestData.supplierAuditorId = this.form.supplierAuditorId
          const response = await createInterestCalculation(requestData)
          const res = response.data
          if (res && res.success && res.data) {
            this.$message.success(res.message || '创建成功')
            this.$router.push(`/finance/interest-calculation-detail/${res.data.id}`)
          } else {
            this.$message.error((res && res.message) || '创建失败')
          }
        } catch (error) {
          const msg = (error.response && error.response.data && error.response.data.message) || error.message || '创建失败'
          this.$message.error(msg)
        } finally {
          this.saveLoading = false
        }
      })
    },
    goBack() {
      this.$router.push('/finance/interest-calculation')
    },
    formatSettlementDate(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleDateString('zh-CN').replace(/\//g, '-')
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    formatContractDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      return d.toLocaleDateString('zh-CN').replace(/\//g, '-')
    },
    formatRateDisplay(contract) {
      if (!contract) return '-'
      if (contract.interestRateType === 'FIXED' && contract.fixedInterestRate != null) {
        const rate = Number(contract.fixedInterestRate)
        return isNaN(rate) ? '-' : `${(rate * 100).toFixed(2)}%`
      }
      if (contract.interestRateType === 'VARIABLE' && contract.variableInterestRates && contract.variableInterestRates.length) {
        return '变动利率（按逾期区间）'
      }
      return '-'
    }
  }
}
</script>

<style lang="scss" scoped>
.interest-calculation-create {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;

  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;

    .title-mark {
      width: 4px;
      height: 18px;
      background: #409eff;
      margin-right: 10px;
    }
  }
}

.form-card,
.info-card,
.preview-card {
  margin-bottom: 20px;
  background: #fff;

  .card-header {
    font-size: 16px;
    font-weight: bold;

    .flow-summary {
      font-size: 13px;
      font-weight: normal;
      color: #909399;
      margin-left: 10px;
    }
  }

  .form-tip {
    font-size: 12px;
    color: #909399;
    margin-left: 10px;
  }
}

.preview-loading {
  padding: 20px;
  text-align: center;
  color: #909399;
}

.preview-error {
  padding: 20px;
  color: #f56c6c;
}

.amount-text {
  color: #303133;
  font-weight: 500;
}

.interest-text {
  color: #e6a23c;
  font-weight: 600;
}

.unpaid-interest {
  color: #f56c6c;
  font-weight: 600;
}

.total-interest {
  margin-top: 16px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 4px;
  text-align: right;

  .label {
    font-size: 14px;
    color: #606266;
  }

  .value {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
    margin-left: 8px;

    &.interest {
      color: #e6a23c;
    }
  }
}

.submit-area {
  padding: 20px;
  background: #fff;
  border-radius: 4px;
}
</style>
